import React, { useRef, useMemo, useEffect, useCallback, useState } from 'react';
import { useOnOutsideClick } from '@wix/da-hooks/pkg/useOnOutsideClick';
import { DropdownStateContext } from './DropdownStateContext';
import { useDropdownState } from '../hooks/useDropdownState';
import { keyEventToHotkey, DropdownHotkey } from '../hotkeys';
// This component manages your dropdown state (open/close) and
// attaches some event handlers. Use if you need to build something custom
const DropdownStateManager = ({ children, openBiData, onOpen, closeBiData, onClose, onToggle, openOnHover, closeOnLeave = openOnHover, disabled, disableKeyboardEvents })=>{
    // state purely used to trigger effect re-run when toggle node changes
    const [toggleNode, setToggleNode] = useState(null);
    const [isMouseOverToggle, setIsMouseOverToggle] = useState(false);
    const [isMouseOverMenu, setIsMouseOverMenu] = useState(false);
    const mouseStateRef = useRef();
    mouseStateRef.current = {
        isMouseOverToggle,
        isMouseOverMenu
    };
    const toggleRef = useRef(null);
    const menuRef = useRef(null);
    const [firstKeyInteraction, setFirstKeyInteraction] = useState(null);
    const { isOpen, openDropdown, closeDropdown, toggleDropdown } = useDropdownState(menuRef, toggleRef, {
        openOnHover,
        closeOnLeave,
        disabled,
        openBiData,
        onOpen,
        closeBiData,
        onClose,
        onToggle
    });
    useOnOutsideClick([
        toggleRef,
        menuRef
    ], closeDropdown);
    const handleCloseDropdown = useCallback(()=>{
        closeDropdown();
        setFirstKeyInteraction(null);
    }, [
        closeDropdown
    ]);
    const handleKeyDown = useCallback((e)=>{
        if ((!toggleRef.current || !toggleRef.current.contains(document.activeElement)) && (!menuRef.current || !menuRef.current.contains(document.activeElement))) {
            return;
        }
        const activeHotkey = keyEventToHotkey(e);
        switch(activeHotkey){
            // Space and enter are handled by the default onClick handler, no need
            // to manually trigger the dropdown here.
            case DropdownHotkey.SPACE:
            case DropdownHotkey.ENTER:
                // This `isOpen` branch allows us to close the dropdown
                // via the keyboard even if it was opened with the mouse
                if (isOpen) {
                    handleCloseDropdown();
                    e.preventDefault();
                } else {
                    setFirstKeyInteraction(activeHotkey);
                    openDropdown();
                    e.preventDefault();
                }
                break;
            case DropdownHotkey.ARROW_UP:
            case DropdownHotkey.ARROW_DOWN:
                if (!isOpen) {
                    setFirstKeyInteraction(activeHotkey);
                    openDropdown();
                    e.preventDefault();
                } else if (isOpen && !firstKeyInteraction) {
                    setFirstKeyInteraction(activeHotkey);
                    e.preventDefault();
                }
                break;
            case DropdownHotkey.HOME:
            case DropdownHotkey.END:
                // HOME and END can't open the dropdown, but we need to
                // support jumping to the first and last item if the dropdown has
                // been opened with the mouse.
                if (isOpen) {
                    setFirstKeyInteraction(activeHotkey);
                    e.preventDefault();
                }
                break;
            // A11Y TODO: Figure out how to allow tabs for StandardMenus inside of dialogs
            case DropdownHotkey.ESCAPE:
            case DropdownHotkey.TAB:
                handleCloseDropdown();
                break;
            default:
        }
    }, [
        handleCloseDropdown,
        isOpen,
        openDropdown,
        firstKeyInteraction
    ]);
    const context = useMemo(()=>({
            isOpen,
            openDropdown,
            closeDropdown: handleCloseDropdown,
            toggleDropdown,
            setToggleRef: (node)=>{
                toggleRef.current = node;
                setToggleNode(node);
            },
            setMenuRef: (node)=>menuRef.current = node,
            firstKeyInteraction
        }), [
        isOpen,
        openDropdown,
        handleCloseDropdown,
        toggleDropdown,
        firstKeyInteraction
    ]);
    const onMouseEnterToggle = useCallback(()=>{
        setIsMouseOverToggle(true);
        if (!isOpen) {
            openDropdown();
        }
    }, [
        isOpen,
        openDropdown,
        setIsMouseOverToggle
    ]);
    const onMouseLeaveToggle = useCallback(()=>{
        setTimeout(()=>{
            var _mouseStateRef_current;
            setIsMouseOverToggle(false);
            if (!((_mouseStateRef_current = mouseStateRef.current) === null || _mouseStateRef_current === void 0 ? void 0 : _mouseStateRef_current.isMouseOverMenu)) {
                closeDropdown();
            }
        }, 1);
    }, [
        closeDropdown
    ]);
    // attach toggle events
    useEffect(()=>{
        const toggle = toggleRef.current;
        if (!toggle || disabled) {
            return;
        }
        toggle.addEventListener('click', toggleDropdown);
        if (!disableKeyboardEvents) {
            toggle.addEventListener('keydown', handleKeyDown);
        }
        if (openOnHover) {
            toggle.addEventListener('mouseenter', onMouseEnterToggle);
        }
        if (closeOnLeave) {
            toggle.addEventListener('mouseleave', onMouseLeaveToggle);
        }
        return ()=>{
            toggle.removeEventListener('click', toggleDropdown);
            if (!disableKeyboardEvents) {
                toggle.removeEventListener('keydown', handleKeyDown);
            }
            if (openOnHover) {
                toggle.removeEventListener('mouseenter', onMouseEnterToggle);
            }
            if (closeOnLeave) {
                toggle.removeEventListener('mouseleave', onMouseLeaveToggle);
            }
        };
    }, [
        onMouseEnterToggle,
        onMouseLeaveToggle,
        toggleDropdown,
        disabled,
        disableKeyboardEvents,
        openOnHover,
        closeOnLeave,
        handleKeyDown,
        toggleNode
    ]);
    const onMouseEnterMenu = useCallback(()=>{
        setIsMouseOverMenu(true);
    }, [
        setIsMouseOverMenu
    ]);
    const onMouseLeaveMenu = useCallback(()=>{
        var _mouseStateRef_current;
        if (!((_mouseStateRef_current = mouseStateRef.current) === null || _mouseStateRef_current === void 0 ? void 0 : _mouseStateRef_current.isMouseOverToggle)) {
            if (closeOnLeave) {
                closeDropdown();
            }
        }
        setIsMouseOverMenu(false);
    }, [
        closeOnLeave,
        closeDropdown
    ]);
    useEffect(()=>{
        setTimeout(()=>{
            const menu = menuRef.current;
            if (!menu) {
                return;
            }
            menu.addEventListener('mouseenter', onMouseEnterMenu);
        }, 1);
    }, [
        isOpen,
        onMouseEnterMenu
    ]);
    // The menu ref element is what gets focused when the dropdown is opened via mouse. In that case, we have to listen for the
    // keydown event there and call the `handleKeyDown` callback accordingly. Focus jumps to the menu after `firstKeyInteraction` gets set,
    // so we remove the listener to prevent duplicate calls to `handleKeyDown`
    useEffect(()=>{
        const menu = menuRef.current;
        if (!menu || disableKeyboardEvents) {
            return;
        }
        if (isOpen && !firstKeyInteraction) {
            menu.addEventListener('keydown', handleKeyDown);
            return ()=>{
                menu.removeEventListener('keydown', handleKeyDown);
            };
        }
    }, [
        disableKeyboardEvents,
        handleKeyDown,
        isOpen,
        firstKeyInteraction,
        onMouseEnterMenu,
        onMouseLeaveMenu
    ]);
    const shouldAddMouseListeners = isOpen && !!menuRef.current;
    useEffect(()=>{
        // for some reason this is thrashing btwn null and the node
        // (maybe https://github.com/facebook/react/issues/4533#issuecomment-261251426)
        // more here: https://react.dev/learn/manipulating-the-dom-with-refs#when-react-attaches-the-refs
        const menu = menuRef.current;
        if (!menu) {
            return;
        }
        if (shouldAddMouseListeners) {
            menu.addEventListener('mouseenter', onMouseEnterMenu);
            menu.addEventListener('mouseleave', onMouseLeaveMenu);
            return ()=>{
                menu.removeEventListener('mouseenter', onMouseEnterMenu);
                menu.removeEventListener('mouseleave', onMouseLeaveMenu);
            };
        }
    }, [
        shouldAddMouseListeners,
        onMouseEnterMenu,
        onMouseLeaveMenu
    ]);
    return /*#__PURE__*/ React.createElement(DropdownStateContext.Provider, {
        value: context
    }, children);
};
DropdownStateManager.displayName = 'DropdownStateManager';
export default DropdownStateManager;

//# sourceMappingURL=DropdownStateManager.js.map